import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/images/Logo.png";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Customer", path: "/customer-landing" },
    { name: "Business", path: "/business" },
    { name: "Download App", path: "/" },
  ];

  // Detect scroll for transparent effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 
        ${
          isScrolled
            ? "bg-white/80 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] border-b border-slate-100/80"
            : "bg-white shadow-sm border-b border-slate-100"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20">
        <div className="flex items-center justify-between h-full">
          {/* Left Section (Logo + Title) */}
          <motion.div
            initial={{ x: -120, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex items-center"
          >
            <div className="w-12 lg:w-20 h-16 flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer">
              <img src={logo} alt="Logo" className="w-full object-contain" />
            </div>
            <h1 className="text-2xl lg:text-3xl text-emerald-700 font-bold tracking-tight hover:text-emerald-800 transition-colors duration-300 cursor-pointer">
              Smartly Orders
            </h1>
          </motion.div>

          {/* Desktop Navigation */}
          <motion.div
            initial={{ x: 120, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            className="hidden md:flex items-center gap-10"
          >
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.path}
                className={
                  item.name === "Download App"
                    ? "px-5 py-2.5 rounded-full text-white font-semibold text-sm bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-md hover:shadow-emerald-500/30 hover:scale-105 transition-all duration-300"
                    : `relative text-md font-semibold text-slate-600 transition-all duration-300 hover:text-emerald-700
                  before:content-[''] before:absolute before:-bottom-0.5 before:left-1/2 before:-translate-x-1/2
                  before:w-0 before:h-0.5 before:bg-emerald-500 before:rounded-full before:transition-all before:duration-300
                  hover:before:w-full`
                }
              >
                {item.name}
              </a>
            ))}
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-slate-100 bg-white"
          >
            <div className="px-4 pt-2 pb-4 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={
                    item.name === "Download App"
                      ? "block px-4 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 shadow-sm mx-2 my-2 text-center text-sm"
                      : "block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors text-sm"
                  }
                >
                  {item.name}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
